import { useEffect, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableField from "./SortableField";
import { Field } from "../types";
import { MuxChevronRight, MuxTrash } from "@/Icons";
import { useFormLayoutLabels } from "@/contexts/translation.context";
export const FormSection = ({
  id,
  title,
  fields,
  isSystem,
  disabled = false,
  onRemove,
  onRemoveField,
  onUpdateTitle,
  className,
}: {
  id: string;
  title: string;
  disabled: boolean;
  onRemove: () => void;
  onRemoveField: (field: Field) => void;
  fields: Array<Field>;
  isSystem: boolean;
  onUpdateTitle: (sectionId: string, newTitle: string) => void;
  className?: string;
}) => {
  const labels = useFormLayoutLabels();
  const [isExpanded, setIsExpanded] = useState(true);
  const refInputName = useRef<HTMLInputElement>(null);
  const { setNodeRef, active, over } = useDroppable({
    id: id,
    data: {
      type: "section",
      sectionId: id,
    },
  });

  useEffect(() => {
    if (id.startsWith("section-")) {
      refInputName.current?.focus();
    }
  }, []);

  return (
    <div className={`form-section ${className || ""}`} ref={setNodeRef}>
      <div className="section-header-2">
        <div className="section-title">
          <button
            className={`icon-button section-toggle transition-ease ${isExpanded ? "expanded" : ""}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <MuxChevronRight />
          </button>
          <input
            id={id}
            ref={refInputName}
            disabled={isSystem}
            type="text"
            value={title.trim() === "" ? "" : title}
            onChange={(e) => onUpdateTitle(id, e.target.value)}
            className={`section-title-input ${title.trim() === "" ? "required" : ""}`}
            placeholder={labels.newSectionPlaceholder}
          />
        </div>
        <div className="section-controls">
          <span className="section-fields-count">{fields.length}</span>
          {!isSystem && (
            <button
              className="icon-button"
              title="Delete section"
              onClick={onRemove}
              disabled={isSystem}
            >
              <MuxTrash />
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="section-content">
          <SortableContext
            items={fields.map((field) => `field-${field.id}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="fields-container-2">
              {!fields?.length ? (
                <div className="no-fields">{labels.noFields}</div>
              ) : (
                fields.map((field) => {
                  // Ensure unique key by combining section id and field id
                  const uniqueKey = `${id}-field-${field.id}`;
                  // Show drop indicator when hovering over this field
                  const isOverThisField = over?.id === `field-${field.id}`;

                  // Check if we're dragging a field (either from section or available)
                  const isDraggingField =
                    active?.data?.current?.type === "field" ||
                    (active?.id && String(active.id).startsWith("available-"));

                  // Show drop indicator if we're dragging a field over this field
                  const isDropTarget = Boolean(
                    isDraggingField &&
                      isOverThisField &&
                      (!active?.data?.current?.field ||
                        active.data.current.field.id !== field.id),
                  );

                  return (
                    // ToDo: fix typescript error
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <SortableField
                      key={uniqueKey}
                      field={field}
                      onRemove={
                        disabled ? undefined : () => onRemoveField(field)
                      }
                      sectionId={id}
                      showDropIndicator={isDropTarget}
                    />
                  );
                })
              )}
            </div>
          </SortableContext>
        </div>
      )}
    </div>
  );
};
